import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  InputAdornment
} from '@mui/material';

class SearchableSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchText: '',
      open: false,
    };
  }

  handleSearchChange = (event) => {
    this.setState({ searchText: event.target.value });
  };

  handleOpen = () => {
    this.setState({ open: true });
  };

  handleClose = () => {
    this.setState({ open: false, searchText: '' });
  };

  handleSelectChange = (event, child) => {
    const nextValue = event && event.target ? event.target.value : undefined;
    // The search row is not an option. Ignore it so callers never see undefined.
    if (nextValue == null) {
      return;
    }
    const matched = (this.props.items || []).some(
      (item) => item && String(item.id) === String(nextValue)
    );
    if (!matched) {
      return;
    }
    this.props.onChange(event, child);
  };

  filterItems = (items, searchText) => {
    if (!searchText || searchText.trim() === '') {
      return items;
    }
    const lowerSearchText = searchText.toLowerCase();
    return items.filter((item) => {
      const haystack = [item.name]
        .concat(item.bins || [])
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(lowerSearchText);
    });
  };

  highlightText = (text, searchText) => {
    if (!searchText || searchText.trim() === '') {
      return <span>{text}</span>;
    }
    
    const lowerText = text.toLowerCase();
    const lowerSearchText = searchText.toLowerCase();
    const startIndex = lowerText.indexOf(lowerSearchText);
    
    if (startIndex === -1) {
      return <span>{text}</span>;
    }
    
    const endIndex = startIndex + searchText.length;
    const beforeMatch = text.substring(0, startIndex);
    const match = text.substring(startIndex, endIndex);
    const afterMatch = text.substring(endIndex);
    
    return (
      <span>
        {beforeMatch}
        <span style={{ 
          backgroundColor: '#e0e0e0',
          fontWeight: 'bold'
        }}>
          {match}
        </span>
        {afterMatch}
      </span>
    );
  };

  render() {
    const {
      label,
      items = [],
      value = '',
      placeholder = "Search...",
      id,
      disabled = false,
      fullWidth = true,
      className,
      sx,
        variant = "standard",
      showLabel = true,
      menuPlacement = "bottom",
      menuZIndex,
      required = false,
      error = false,
    } = this.props;

    const { searchText, open } = this.state;
    const filteredItems = this.filterItems(items, searchText);
    
    // Match by string so localStorage IDs still select when item.id type differs
    const matchedItem = (items || []).find(
      (item) => item && value != null && value !== '' && String(item.id) === String(value)
    );
    const safeValue = matchedItem ? matchedItem.id : '';
    const outlined = variant === 'outlined';
    const labelId = `${id}-label`;

    return (
      <FormControl 
        sx={{ width: fullWidth ? '100%' : 'auto', ...sx }} 
        className={className}
        disabled={disabled}
        variant={variant}
        error={error}
        required={required}
      >
        {showLabel && (
          <InputLabel
            id={labelId}
            htmlFor={id}
            shrink={outlined ? true : undefined}
            required={required}
            error={error}
            sx={{ '& .MuiFormLabel-asterisk': { color: '#d32f2f' } }}
          >
            {label}
          </InputLabel>
        )}
        <Select
          open={open}
          onOpen={this.handleOpen}
          onClose={this.handleClose}
          value={safeValue}
          onChange={this.handleSelectChange}
          labelId={showLabel ? labelId : undefined}
          label={outlined && showLabel ? label : undefined}
          notched={outlined && showLabel ? true : undefined}
          displayEmpty={outlined}
          inputProps={{
            name: id,
            id: id,
          }}
          variant={variant}
          MenuProps={{
            style: menuZIndex ? { zIndex: menuZIndex } : undefined,
            PaperProps: {
              style: {
                maxHeight: 280,
                zIndex: menuZIndex || undefined,
              },
            },
            autoFocus: false,
            disableAutoFocusItem: true,
            anchorOrigin: {
              vertical: menuPlacement === 'top' ? 'top' : 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: menuPlacement === 'top' ? 'bottom' : 'top',
              horizontal: 'left',
            },
            marginThreshold: 8,
          }}
        >
          <ListSubheader
            // Not a MenuItem: the padding under the field has no value, and
            // selecting it sends undefined to onChange. disabled keeps MenuList
            // from giving this row a tabIndex, which is what makes a click select.
            disabled
            disableGutters
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1,
              lineHeight: 'unset',
              borderBottom: '1px solid #e0e0e0',
              padding: '8px 16px',
              cursor: 'default',
            }}
          >
            <TextField
              size="small"
              placeholder={placeholder}
              fullWidth
              value={searchText}
              onChange={this.handleSearchChange}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span role="img" aria-label="search" style={{ color: '#757575' }}>🔍</span>
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <span 
                      role="button"
                      tabIndex={0}
                      onClick={() => this.setState({ searchText: '' })}
                      style={{
                        cursor: 'pointer',
                        color: '#757575',
                        fontSize: '18px',
                        lineHeight: 1,
                        padding: '0 4px'
                      }}
                    >
                      ×
                    </span>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#eeeeee',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                  }
                }
              }}
            />
          </ListSubheader>
          {filteredItems.map(item => (
            <MenuItem key={item.id} value={item.id}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minWidth: 0 }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {this.highlightText(item.name, searchText)}
                </span>
                {Array.isArray(item.bins) && item.bins.length
                  ? item.bins.map((bin) => (
                      <span
                        key={bin}
                        style={{
                          flexShrink: 0,
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: '#e7f4f5',
                          color: '#0a5d66',
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: '18px',
                        }}
                      >
                        {bin}
                      </span>
                    ))
                  : null}
              </span>
            </MenuItem>
          ))}
          {filteredItems.length === 0 && (
            <MenuItem disabled style={{ justifyContent: 'center', color: '#757575' }}>
              No {label.toLowerCase()}s found
            </MenuItem>
          )}
        </Select>
      </FormControl>
    );
  }
}

SearchableSelect.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    bins: PropTypes.arrayOf(PropTypes.string),
  })).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  id: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  sx: PropTypes.object,
  variant: PropTypes.string,
  showLabel: PropTypes.bool,
  menuPlacement: PropTypes.oneOf(['top', 'bottom']),
  menuZIndex: PropTypes.number,
  required: PropTypes.bool,
  error: PropTypes.bool,
};

export default SearchableSelect;



// WEBPACK FOOTER //
// ./src/components/common/SearchableSelect.js